import { ChangeDetectionStrategy, Component, computed, input, ViewChild } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { Chart } from 'chart.js';
import type { ChartConfiguration, ChartOptions } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import type { TiltDisplacement } from '../../interfaces';

Chart.register(annotationPlugin);

@Component({
  selector: 'app-tilt-displacement-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BaseChartDirective],
  template: `
    <div class="h-[400px] w-full">
      <canvas baseChart
              [data]="chartData()"
              [options]="chartOptions()"
              [type]="'line'">
      </canvas>
    </div>
  `
})
export class TiltDisplacementChartComponent {
  public displacements = input.required<TiltDisplacement[]>();
  public title = input<string>('傾斜変位');
  public alertThreshold = input<number>(1.0);

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  public readonly chartData = computed<ChartConfiguration['data']>(() => {
    const data = this.displacements();
    // Sort by depth (assuming deeper is higher number, we want deep at bottom but we'll use reverse: true on axis)
    const sorted = [...data].sort((a, b) => a.depth - b.depth);

    return {
      labels: sorted.map(d => d.depth.toString()), // Y-axis labels when indexAxis='y'
      datasets: [
        {
          data: sorted.map(d => d.displacementX),
          label: '変位X (mm)',
          borderColor: '#1976d2',
          backgroundColor: 'rgba(25, 118, 210, 0.2)',
          pointBackgroundColor: '#1976d2',
          tension: 0.1
        },
        {
          data: sorted.map(d => d.displacementY),
          label: '変位Y (mm)',
          borderColor: '#e64a19',
          backgroundColor: 'rgba(230, 74, 25, 0.2)',
          pointBackgroundColor: '#e64a19',
          tension: 0.1
        }
      ]
    };
  });

  public readonly chartOptions = computed<ChartOptions>(() => {
    const threshold = this.alertThreshold();
    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y', // Depth on Y axis, displacement on X axis
      scales: {
        y: {
          reverse: true, // Deeper depths at the bottom
          title: {
            display: true,
            text: '深度 (m)'
          }
        },
        x: {
          title: {
            display: true,
            text: '変位量 (mm)'
          },
          grid: {
            color: (ctx) => {
              if (ctx.tick.value === 0) return '#000000'; // Emphasize zero line
              return '#e0e0e0';
            },
            lineWidth: (ctx) => {
              if (ctx.tick.value === 0) return 2;
              return 1;
            }
          }
        }
      },
      plugins: {
        title: {
          display: false
        },
        legend: {
          position: 'bottom'
        },
        annotation: {
          annotations: {
            line1: {
              type: 'line',
              xMin: threshold,
              xMax: threshold,
              borderColor: 'rgba(255, 99, 132, 0.5)',
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: `管理基準(+${threshold}mm)`,
                position: 'start'
              }
            },
            line2: {
              type: 'line',
              xMin: -threshold,
              xMax: -threshold,
              borderColor: 'rgba(255, 99, 132, 0.5)',
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: `管理基準(-${threshold}mm)`,
                position: 'start'
              }
            }
          }
        }
      }
    };
  });
}
